import { useEffect, useState } from "react";
import { api } from "../api";

// ── Asset Library — Hook / Body / Packshot havuzları ────────────────────────
// Asset'ler Ürün Ayarları'nda tanımlanan Drive klasörlerinden okunur (upload
// burada yapılmıyor — kaynak Drive). Hook Body Avatar üretimi bu havuzlardan
// feature'a göre rastgele 1 hook + 1 body seçer (bkz. edit-runner.ts).

const ACCENT = "#5E6AD2";
const API_ORIGIN = (import.meta.env.VITE_API_BASE ?? "/api").replace(/\/$/, "").replace(/\/api$/, "");

function st(css: string): React.CSSProperties {
  const obj: Record<string, string> = {};
  css.split(";").forEach((decl) => {
    const idx = decl.indexOf(":");
    if (idx === -1) return;
    const prop = decl.slice(0, idx).trim();
    const val = decl.slice(idx + 1).trim();
    if (!prop || !val) return;
    const camel = prop.replace(/-([a-z])/g, (_, c) => c.toUpperCase());
    obj[camel] = val;
  });
  return obj as React.CSSProperties;
}

function H({
  tag: Tag = "div", baseStyle, hoverStyle, ...rest
}: { tag?: any; baseStyle: React.CSSProperties; hoverStyle?: React.CSSProperties; [k: string]: any }) {
  const [hover, setHover] = useState(false);
  return (
    <Tag
      {...rest}
      style={{ ...baseStyle, ...(hover ? hoverStyle : {}) }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    />
  );
}

type Pool = "hook" | "body" | "packshot";
const POOLS: { key: Pool; label: string; icon: string; desc: string }[] = [
  { key: "hook", label: "Hooks", icon: "🪝", desc: "İlk saniyede oynayan dikkat çekici klipler" },
  { key: "body", label: "Bodies", icon: "🎬", desc: "Avatar dekupe edilip devam ederken oynayan klipler" },
  { key: "packshot", label: "Packshots", icon: "📦", desc: "Kapanış / ürün görseli klipleri" },
];

function isVideo(name: string) {
  return /\.(mp4|mov|webm)$/i.test(name);
}

export function AssetLibrary({ productId }: { productId: string }) {
  const [pool, setPool] = useState<Pool | null>(null);

  return (
    <div style={st("flex:1;overflow-y:auto")}>
      <div style={st("max-width:960px;margin:0 auto;padding:40px 48px 64px")}>
        <div style={st("margin-bottom:22px")}>
          <h1 style={st("margin:0 0 6px;font-size:26px;font-weight:600;letter-spacing:-.02em")}>🗂️ Asset Library</h1>
          <p style={st("margin:0;font-size:14.5px;color:#6b6b76")}>Hook / Body / Packshot havuzları — Drive klasörlerinden otomatik okunur. Klasörleri Ürün Ayarları'ndan bağla.</p>
        </div>

        {!pool ? (
          <div style={st("display:grid;grid-template-columns:repeat(3,1fr);gap:14px")}>
            {POOLS.map((p) => (
              <H key={p.key} onClick={() => setPool(p.key)}
                baseStyle={st("background:#fff;border:1px solid rgba(20,20,30,.08);border-radius:12px;padding:22px 18px;cursor:pointer;transition:border-color .12s,box-shadow .12s")}
                hoverStyle={st("border-color:rgba(20,20,30,.18);box-shadow:0 2px 10px rgba(20,20,30,.06)")}
              >
                <div style={st("font-size:28px;margin-bottom:10px")}>{p.icon}</div>
                <div style={st("font-size:15px;font-weight:600;margin-bottom:4px")}>{p.label}</div>
                <div style={st("font-size:12.5px;color:#8a8a93;line-height:1.5")}>{p.desc}</div>
              </H>
            ))}
          </div>
        ) : (
          <PoolView productId={productId} pool={pool} onBack={() => setPool(null)} />
        )}
      </div>
    </div>
  );
}

function PoolView({ productId, pool, onBack }: { productId: string; pool: Pool; onBack: () => void }) {
  const [groups, setGroups] = useState<{ feature: string | null; matchesFeature: boolean; files: { id: string; name: string; mimeType: string }[] }[] | null>(null);
  const [note, setNote] = useState<string | undefined>();
  const [loading, setLoading] = useState(true);
  const meta = POOLS.find((p) => p.key === pool)!;

  useEffect(() => {
    setLoading(true);
    api.getAssetPool(productId, pool)
      .then((r) => { setGroups(r.groups); setNote(r.note); })
      .catch(() => { setGroups([]); setNote("Yüklenirken hata oluştu."); })
      .finally(() => setLoading(false));
  }, [productId, pool]);

  return (
    <div>
      <H tag="button" onClick={onBack}
        baseStyle={st("display:flex;align-items:center;gap:6px;padding:6px 4px;margin-bottom:16px;border:none;background:transparent;color:#6b6b76;font-family:inherit;font-size:13px;cursor:pointer")}
        hoverStyle={{ color: ACCENT }}
      >
        ← Havuzlara dön
      </H>
      <h2 style={st("font-size:18px;font-weight:600;margin:0 0 4px;display:flex;align-items:center;gap:8px")}>{meta.icon} {meta.label}</h2>

      {loading ? (
        <div style={st("padding:40px 0;text-align:center;color:#9a9aa2;font-size:13px")}>Yükleniyor…</div>
      ) : !groups || groups.length === 0 ? (
        <div style={st("padding:30px;text-align:center;color:#9a9aa2;font-size:13px;background:#fff;border:1px solid rgba(20,20,30,.08);border-radius:11px;margin-top:14px")}>
          {note ?? "Bu havuzda henüz asset yok."}
        </div>
      ) : (
        <div style={st("display:flex;flex-direction:column;gap:18px;margin-top:16px")}>
          {groups.map((g, i) => (
            <div key={i}>
              <div style={st("display:flex;align-items:center;gap:8px;margin-bottom:8px")}>
                <span style={{ ...st("font-size:10.5px;font-weight:700;letter-spacing:.04em;border-radius:5px;padding:2px 7px"), color: g.matchesFeature ? ACCENT : "#8a8a93", background: g.matchesFeature ? `color-mix(in srgb,${ACCENT} 10%,#fff)` : "rgba(20,20,30,.05)" }}>
                  {g.feature ?? "GENEL"}
                </span>
                <span style={st("font-size:12px;color:#9a9aa2")}>{g.files.length} dosya</span>
              </div>
              <div style={st("display:grid;grid-template-columns:repeat(auto-fill,minmax(140px,1fr));gap:10px")}>
                {g.files.map((f) => (
                  <div key={f.id} style={st("background:#fff;border:1px solid rgba(20,20,30,.08);border-radius:9px;overflow:hidden")}>
                    {isVideo(f.name) ? (
                      <video src={`${API_ORIGIN}/proxy/media/${f.id}?ct=video/mp4`} controls preload="metadata" style={st("width:100%;height:110px;object-fit:cover;background:#000;display:block")} />
                    ) : (
                      <img src={`${API_ORIGIN}/proxy/media/${f.id}?ct=image/png`} style={st("width:100%;height:110px;object-fit:cover;display:block")} />
                    )}
                    <div style={st("font-size:10.5px;color:#6b6b76;padding:6px 8px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis")} title={f.name}>{f.name}</div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
